import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, PlaneLanding, PlaneTakeoff } from "lucide-react";
import { toast } from "sonner";
import DateField from "./DateField";
import { genId } from "@/lib/samples";
import { useT } from "@/lib/i18n";

export default function AddTripForm({ onAdd, defaultYear }) {
  const { t } = useT();
  const [entry, setEntry] = useState(null);
  const [exit, setExit] = useState(null);
  const [openTrip, setOpenTrip] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const entryRef = useRef(null);
  const exitRef = useRef(null);
  const focusOnMount = useRef(null); // 'entry' | 'exit' | null

  // After the form remounts (formKey change), focus the requested input.
  useEffect(() => {
    if (!focusOnMount.current) return;
    const target = focusOnMount.current === "exit" ? exitRef : entryRef;
    focusOnMount.current = null;
    // Defer to next tick so the re-mounted Input is in the DOM.
    const t = setTimeout(() => target.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [formKey]);

  // Core add function - can be called with override values so that Enter-to-submit
  // works even before React has re-rendered the parent state.
  const doAdd = (overrideEntry, overrideExit, overrideOpen) => {
    const e = overrideEntry !== undefined ? overrideEntry : entry;
    const x = overrideExit !== undefined ? overrideExit : exit;
    const isOpen = overrideOpen !== undefined ? overrideOpen : openTrip;

    if (!e) {
      toast.error(t("add.needEntry"));
      return;
    }
    if (!isOpen && !x) {
      toast.error(t("add.needExit"));
      return;
    }
    if (!isOpen && x && x < e) {
      toast.error(t("add.exitBeforeEntry"));
      return;
    }
    onAdd({ id: genId(), entry: e, exit: isOpen ? null : x });
    toast.success(t("add.saved"));
    setEntry(null);
    setExit(null);
    setOpenTrip(false);
    // Force DateField remount so their internal text state resets even when
    // value went from something -> null within the same React batch.
    // Also request focus back to entry for the next quick input.
    focusOnMount.current = "entry";
    setFormKey((k) => k + 1);
  };

  const submit = () => doAdd();

  // Enter in ENTRY input:
  //  - if openTrip is on -> submit immediately with just the entry date
  //  - otherwise -> move focus to EXIT field so user can keep typing
  const onEntryEnter = (iso) => {
    if (openTrip) {
      doAdd(iso, null, true);
    } else {
      exitRef.current?.focus();
    }
  };

  // Enter in EXIT input always attempts to submit; focus returns to entry.
  const onExitEnter = (iso) => {
    doAdd(entry, iso, false);
  };

  return (
    <Card
      className="bg-[#131620] border-[#272A35] rounded-xl p-5 md:p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] w-full h-full flex flex-col"
      data-testid="add-trip-form"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-serif-display text-xl text-white tracking-tight">
          {t("add.title")}
        </h3>
        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t("add.subtitle")}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-400 flex items-center gap-1.5">
            <PlaneLanding className="h-3.5 w-3.5 text-[#D4AF37]" />
            {t("add.entry")}
          </Label>
          <DateField
            key={`entry-${formKey}`}
            value={entry}
            onChange={setEntry}
            testId="entry-date"
            defaultYear={defaultYear}
            onEnter={onEntryEnter}
            inputRef={entryRef}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-400 flex items-center gap-1.5">
            <PlaneTakeoff className="h-3.5 w-3.5 text-[#D4AF37]" />
            Izlazak
          </Label>
          <div className={openTrip ? "opacity-40 pointer-events-none" : ""}>
            <DateField
              key={`exit-${formKey}`}
              value={exit}
              onChange={setExit}
              testId="exit-date"
              minDate={entry}
              defaultYear={defaultYear}
              onEnter={onExitEnter}
              inputRef={exitRef}
            />
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Switch
            id="open-trip"
            checked={openTrip}
            onCheckedChange={setOpenTrip}
            data-testid="open-trip-switch"
          />
          <Label
            htmlFor="open-trip"
            className="text-sm text-slate-300 cursor-pointer"
          >
            {t("add.openTrip")}
          </Label>
        </div>

        <Button
          onClick={submit}
          className="bg-[#D4AF37] hover:bg-[#FDE047] text-black font-semibold rounded-md w-full sm:w-auto"
          data-testid="add-trip-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          {t("add.button")}
        </Button>
      </div>
    </Card>
  );
}
